import { Injectable, WritableSignal, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Subject } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { KycApi } from 'src/app/shared/apis/kyc-basic.api';
import { KycInforList, KycLevelInfor, PrimaryKycForm, UserBasicInfor } from 'src/app/shared/interfaces/kyc.interface';
import { LocaleService } from 'src/app/shared/service/locale.service';
import { ToastService } from 'src/app/shared/service/toast.service';
import { KycUtils } from './kyc-contants/kyc-utils';

@Injectable({
  providedIn: 'root',
})
export class KycService {
  constructor(
    private kycApi: KycApi,
    private localeService: LocaleService,
    private kycUtils: KycUtils,
    private appService: AppService,
    private toast: ToastService,
  ) {}

  orderStepSubject: BehaviorSubject<number> = new BehaviorSubject(1); // 认证等级页面  1:基础中国 2:基础非中国 3:中级中国 4:中级非中国
  showMidChinaPage: BehaviorSubject<any> = new BehaviorSubject(true); // 中级验证中国区页面
  dialogStepSubject: BehaviorSubject<number> = new BehaviorSubject(1); // 1:中级验证-账户验证 2:证件信息不符 3:升级审核中 4:基础验证成功 5:手机号说明
  carouselPageSubject: BehaviorSubject<number> = new BehaviorSubject(1); // 当前carousel页码 1:第一页 2:第二页
  kycStatus: BehaviorSubject<any> = new BehaviorSubject({}); //kyc用户等级信息
  bankCardSubject: BehaviorSubject<any> = new BehaviorSubject(null); //选择银行卡类型
  kycInforList: BehaviorSubject<KycInforList | null> = new BehaviorSubject<KycInforList | null>(null); //kyc用户等级权限
  refreshKycSubject$: Subject<boolean> = new Subject(); //刷新数据
  kycLevel$: BehaviorSubject<KycLevelInfor | null> = new BehaviorSubject<KycLevelInfor | null>(null); //用户当前最搞kyc等级信息

  /** 订阅表单数据 */
  _primaryFormData: WritableSignal<PrimaryKycForm | null> = signal({});
  primaryFormData$ = toObservable(this._primaryFormData);

  /** 海外 初级 kyc 表单 缓存*/
  foreiPrimaryKycForm = {
    fullName: {
      value: '',
      error: false,
      errorText: '',
    },
    firstName: {
      value: '',
      error: false,
      errorText: '',
    },
    lastName: {
      value: '',
      error: false,
      errorText: '',
    },
    userBorth: {
      value: '',
    },
    email: {
      value: '',
      error: false,
      errorText: '',
      boundEmail: false,
    },
    address: {
      value: '',
    },
    zipCode: {
      value: '',
    },
    city: {
      value: '',
    },
    phone: {
      value: '',
      boundPhone: false,
    },
  };

  /** 中国 初级 kyc 表单 缓存 */
  cnPrimaryKycForm = {
    fullName: {
      value: '',
      error: false,
      errorText: '',
    },
    phone: {
      value: '',
      boundPhone: false,
    },
  };

  /** 需要输入全名的国家全名称国家 中国，香港，台湾，澳门，越南，泰国，马来西亚 */
  get fullNameModeCountryList() {
    return JSON.parse(this.appService.tenantConfig?.config.fullNameCountries || '[]');
  }

  /** reset 中国 初级KYC表单*/
  onResetKycForm() {
    // 重置 中国 表单
    this.cnPrimaryKycForm.fullName.value = '';
    this.cnPrimaryKycForm.fullName.error = false;
    this.cnPrimaryKycForm.fullName.errorText = '';
    this.cnPrimaryKycForm.phone.value = '';
    this.cnPrimaryKycForm.phone.boundPhone = false;
  }

  /** 重置名 姓 */
  onResetFLName() {
    this.foreiPrimaryKycForm.firstName.value = '';
    this.foreiPrimaryKycForm.firstName.error = false;
    this.foreiPrimaryKycForm.lastName.value = '';
    this.foreiPrimaryKycForm.lastName.error = false;
    this.foreiPrimaryKycForm.phone.value = '';
    this.foreiPrimaryKycForm.address.value = '';
    this.foreiPrimaryKycForm.city.value = '';
    this.foreiPrimaryKycForm.zipCode.value = '';
  }

  /** 重置全名 */
  onResetFullName() {
    this.foreiPrimaryKycForm.fullName.value = '';
    this.foreiPrimaryKycForm.fullName.error = false;
    this.foreiPrimaryKycForm.phone.value = '';
    this.foreiPrimaryKycForm.address.value = '';
    this.foreiPrimaryKycForm.city.value = '';
    this.foreiPrimaryKycForm.zipCode.value = '';
  }

  //获取用户kyc状态,
  async getKycInfor() {
    const kycInfo = await this.kycApi.getKycStatus();
    if (kycInfo && kycInfo.success) {
      return kycInfo.data;
    }
    return [];
  }

  //获取用户kyc权限
  async getKycSettings() {
    const kycSettingsInfo: any = await this.kycApi.getKycSettings();
    if (kycSettingsInfo?.data) {
      return kycSettingsInfo.data;
    }
    return {
      kycLimit: [],
    };
  }

  async getMemberKycLimit() {
    const memberKycInfo: any = await this.kycApi.getMemberKycLimit();
    if (memberKycInfo?.data) {
      return memberKycInfo.data;
    }
    return {};
  }

  /**
   * 检测kyc状态，返回固定内容
   *
   * @param kycStatusInfor kyc原始信息数组
   * @returns
   * level：number ,kyc等级 0 | 1 | 2 | 3
   * kycStatusName：string ,kyc状态名称
   * 其余为当前kyc原本的属性
   */
  checkUserKycStatus(kycStatusInfor: any[] = []): KycLevelInfor {
    let current = kycStatusInfor[0];
    let level = 0;
    let kycStatusName = '';
    const levelMap: any = {
      KycPrimary: 1,
      KycIntermediat: 2,
      KycAdvanced: 3,
    };
    kycStatusInfor.forEach(v => {
      if (v?.status === 'S' && levelMap[v.type ?? 'KycPrimary'] > level) {
        current = v;
        level = levelMap[v.type ?? 'KycPrimary'];
      }
    });
    kycStatusName = [
      this.localeService.getValue('no_cer'),
      this.localeService.getValue('pri_ceri'),
      this.localeService.getValue('inter_ceri'),
      this.localeService.getValue('ad_ceri'),
    ][level];
    return { level, kycStatusName, ...(current?.status === 'S' ? current : { type: '', value: '' }) };
  }

  /**
   * 检测kyclevel，返回kyclevel
   *
   * @param kycGrade
   * @returns
   * level：number ,kyc等级 0 | 1 | 2 | 3
   */
  checkUserKycLevel(kycGrade: string) {
    const level = 0;
    const levelMap: any = {
      KycPrimary: 1,
      KycIntermediat: 2,
      KycAdvanced: 3,
    };
    const kycStatusName = [
      this.localeService.getValue('no_cer'),
      this.localeService.getValue('pri_ceri'),
      this.localeService.getValue('inter_ceri'),
      this.localeService.getValue('ad_ceri'),
    ][level];
    return { level: levelMap[kycGrade] || 0, kycStatusName, name: kycGrade };
  }

  /**
   * kyc限制权限数据组合
   *
   * @param kycLimit kyc权限list原始信息数组
   * @param kycStatusInfor kyc状态原始信息数组
   * @returns
   *
   * kyc限制权限数据列表
   */
  KycLimitList(kycStatusInfor: any[] = [], kycLimit: any[] = []) {
    const iniConfig = this.kycUtils.getCardInfoConfig();

    iniConfig[0].discrible = kycLimit.find(e => e.kycType == 'KycPrimary')?.title;
    iniConfig[0].acInforConfig = kycLimit.find(e => e.kycType == 'KycPrimary');
    iniConfig[1].discrible = kycLimit.find(e => e.kycType == 'KycIntermediat')?.title;
    iniConfig[1].acInforConfig = kycLimit.find(e => e.kycType == 'KycIntermediat');
    iniConfig[2].discrible = kycLimit.find(e => e.kycType == 'KycAdvanced')?.title;
    iniConfig[2].acInforConfig = kycLimit.find(e => e.kycType == 'KycAdvanced');
    const primary = kycStatusInfor.find((item: any) => item.type === 'KycPrimary');
    if (primary != null) {
      iniConfig[0].status = primary['status'];
      //通过初始，开启中级
      if (primary['status'] == 'S') {
        iniConfig[1].status = 'I';
      }
    }
    const intermediate = kycStatusInfor.find((item: any) => item.type === 'KycIntermediat');
    if (intermediate != null) {
      iniConfig[1].status = intermediate['status'];

      //通过中级，开启高级
      if (intermediate['status'] == 'S') {
        iniConfig[2].status = 'I';
      }
    }
    const advance = kycStatusInfor.find((item: any) => item.type === 'KycAdvanced');
    if (advance != null) {
      iniConfig[2].status = advance['status'];
    }

    return iniConfig;
  }

  /**
   * kyc认证按钮状态数据处理
   *
   * @param status 当前kyc状态
   * @param id kyclist id
   * @returns
   */
  kycSubmitBtnFormat(status: string, id: number) {
    switch (status) {
      case 'I': // intial
        return {
          buttonText: 'start_now',
          btnClass: 'initial-btn',
        };

      case 'P': // pending
        return {
          buttonText: 'refunding',
          btnClass: 'pending-btn',
        };
      case 'S': // pass
        return {
          buttonText: 'cer',
          btnClass: 'pass-btn',
        };
      case 'R': // reject  失败
        return {
          buttonText: 'veri_again',
          btnClass: 'initial-btn',
        };

      case 'U': // 需完成认证
      default:
        if (id === 0) {
          return {
            btnClass: 'invalid-btn',
            buttonText: 'unavailable',
          };
        }
        if (id === 1) {
          return {
            btnClass: 'invalid-btn',
            buttonText: 'unavailable',
          };
        }
        if (id === 2) {
          return {
            btnClass: 'invalid-btn',
            buttonText: 'unavailable',
          };
        }
        throw new Error(`${this.localeService.getValue('unable_proce00')}：${id}`);
    }
  }

  /**
   * 用户基本信息
   *
   * @returns
   */
  async getMemberBasicInfor(): Promise<UserBasicInfor | null> {
    const apiData = await this.kycApi.getMemberBasicInfo();
    if (apiData?.success && apiData?.data) {
      return apiData?.data;
    } else {
      return null;
    }
  }

  /**
   * 驗證文件格式與大小
   *
   * @param file
   * @param target
   */
  verifyImageFile(target: HTMLInputElement) {
    const file = target.files && target.files[0];
    if (!file) return;

    // 文件类型验证
    const fileType = file.type.toLowerCase();

    if (!['image/png', 'image/jpg', 'image/jpeg', 'application/pdf'].includes(fileType)) {
      this.toast.show({ message: this.localeService.getValue('unsupp_file'), type: 'fail', title: '' });
      target.value = '';
      return;
    }
    // 文件大小验证 10KB - 5MB
    // file_limerr:文件太大,请重新上传
    // file_size:档案大小必须在10KB到5120KB之间并采用 jpg／.jpeg／.png 格式
    const fileMunSize = 1000 * 10;
    const fileMaxSize = 1000 * 1000 * 5;
    if (file.size > fileMaxSize) {
      this.toast.show({ message: this.localeService.getValue('file_limerr'), type: 'fail', title: '' });
      target.value = '';
      return;
    }
    if (file.size < fileMunSize) {
      this.toast.show({ message: this.localeService.getValue('file_too_small'), type: 'fail', title: '' });
      target.value = '';
      return;
    }
  }
}
